import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useContext, useEffect, useMemo, useState } from 'react';
import { APIContext } from '../../Contexts/APIContext';
import SingleFileUpload from '../../Utilities/SingleFileUpload';
import MaintenanceDialogTitle from './MaintenanceDialogTitle';
import { BulkSiteDeletionRun, ReleaseVersion } from './ReleaseManagementTypes';

type Props = { open: boolean; onClose: () => void };

type Preview = {
    version: ReleaseVersion;
    uploadedRows: number;
    sitesToDelete: string[];
    missingFromVersion: string[];
    invalidReferences: string[];
    duplicateReferences: string[];
    totalRecordCount: number;
    scheduledLocal: string;
    startsImmediately: boolean;
    hasActiveRun: boolean;
};

const displayDate = (value?: string | null) => value ? new Date(value).toLocaleString('en-GB', {
    timeZone: 'Europe/London', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
}) : '—';

const statusColour = (status: BulkSiteDeletionRun['status']): 'default' | 'info' | 'success' | 'warning' | 'error' => {
    if (status === 'Scheduled') return 'info';
    if (status === 'Running' || status === 'CompletedWithFailures') return 'warning';
    if (status === 'Completed') return 'success';
    if (status === 'Failed') return 'error';
    return 'default';
};

const ReferenceSummary = ({ label, values }: { label: string; values: string[] }) => values.length > 0 ? <Alert severity="error">
    <strong>{label} ({values.length}):</strong> {values.slice(0, 12).join(', ')}
    {values.length > 12 ? `, and ${values.length - 12} more` : ''}
</Alert> : null;

export default function BulkDeleteSitesDialog({ open, onClose }: Props) {
    const { get, post, showMessage } = useContext(APIContext);
    const [versions, setVersions] = useState<ReleaseVersion[]>([]);
    const [versionId, setVersionId] = useState(0);
    const [file, setFile] = useState<File | null>(null);
    const [preview, setPreview] = useState<Preview | null>(null);
    const [runs, setRuns] = useState<BulkSiteDeletionRun[]>([]);
    const [acknowledged, setAcknowledged] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const futureVersions = useMemo(
        () => versions.filter(version => !version.isCurrent && new Date(version.start).getTime() > Date.now()),
        [versions]
    );
    const reloadRuns = () => get('Admin', 'GetBulkSiteDeletionRuns', setRuns);
    const resetReview = () => {
        setPreview(null);
        setAcknowledged(false);
        setConfirming(false);
    };

    useEffect(() => {
        if (!open) return;
        setFile(null);
        resetReview();
        get('Admin', 'GetReleaseVersions', (items: ReleaseVersion[]) => {
            setVersions(items);
            const firstFuture = items.find(version => !version.isCurrent && new Date(version.start).getTime() > Date.now());
            setVersionId(firstFuture?.id ?? 0);
        });
        reloadRuns();
    }, [open]);

    const formData = (includeAcknowledgement = false) => {
        const data = new FormData();
        if (file) data.append('file', file);
        data.append('versionId', versionId.toString());
        if (includeAcknowledgement) data.append('acknowledged', acknowledged.toString());
        return data;
    };

    const review = async () => {
        if (!file) return;
        setSubmitting(true);
        await post('Admin', 'PreviewBulkSiteDeletion', formData(), setPreview);
        setSubmitting(false);
    };

    const schedule = async () => {
        if (!file) return;
        setSubmitting(true);
        await post('Admin', 'ScheduleBulkSiteDeletion', formData(true), () => {
            showMessage('success', `${preview?.sitesToDelete.length ?? 0} sites were scheduled for deletion.`);
            setConfirming(false);
            setFile(null);
            resetReview();
            reloadRuns();
        });
        setSubmitting(false);
    };

    const close = () => {
        if (submitting) return;
        setFile(null);
        resetReview();
        onClose();
    };

    const hasValidationErrors = !!preview && (
        preview.invalidReferences.length > 0 || preview.duplicateReferences.length > 0 || preview.missingFromVersion.length > 0
    );
    const canReview = !!file && versionId > 0 && !submitting;
    const canSchedule = !!preview && preview.sitesToDelete.length > 0 && !preview.hasActiveRun &&
        !hasValidationErrors && acknowledged && !submitting;

    return <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
        <MaintenanceDialogTitle icon={<DeleteSweepOutlinedIcon />} title="Delete selected sites"
            description="Strictly validate a CSV and schedule deletion from one future version" tone="error" />
        <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
                <Alert severity="error">
                    This permanently deletes the selected sites and every related record from the chosen future version. Current and historic versions cannot be selected.
                </Alert>
                <Alert severity="info">
                    Use a CSV with a <strong>Reference</strong> column, or references in the first column. Any invalid, duplicate, or missing reference blocks the entire schedule.
                </Alert>
                <TextField select fullWidth label="Future data version" value={versionId}
                    disabled={submitting || futureVersions.length === 0}
                    onChange={event => { setVersionId(Number(event.target.value)); resetReview(); }}>
                    {futureVersions.map(version => <MenuItem key={version.id} value={version.id}>{version.versionName}</MenuItem>)}
                </TextField>
                {futureVersions.length === 0 && <Alert severity="warning">There are no future data versions available.</Alert>}
                <SingleFileUpload file={file} acceptedTypes=".csv,text/csv"
                    onDrop={newFile => { setFile(newFile); resetReview(); }}
                    onDelete={() => { setFile(null); resetReview(); }} />

                {preview && <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={1.25}>
                        <Typography variant="h6">CSV review</Typography>
                        <Typography>
                            <strong>{preview.sitesToDelete.length}</strong> of {preview.uploadedRows} uploaded rows identify sites in <strong>{preview.version.versionName}</strong>, affecting <strong>{preview.totalRecordCount}</strong> records.
                        </Typography>
                        <Typography variant="body2">{preview.startsImmediately
                            ? 'Starts immediately because 22:00 UK time has passed.'
                            : `Scheduled start: ${preview.scheduledLocal} (Europe/London)`}</Typography>
                        {preview.hasActiveRun && <Alert severity="error">Another Copy, Move, or Bulk Delete is active for this version.</Alert>}
                        <ReferenceSummary label="Not found in selected version" values={preview.missingFromVersion} />
                        <ReferenceSummary label="Invalid references" values={preview.invalidReferences} />
                        <ReferenceSummary label="Duplicate references" values={preview.duplicateReferences} />
                        {hasValidationErrors && <Alert severity="error">Deletion cannot be scheduled until every CSV entry passes validation.</Alert>}
                    </Stack>
                </Paper>}

                {preview && <FormControlLabel
                    control={<Checkbox checked={acknowledged} onChange={event => setAcknowledged(event.target.checked)} />}
                    label="I understand these sites and all related records will be permanently deleted from the selected future version."
                />}

                <Divider />
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">Recent bulk deletion runs</Typography>
                    <Tooltip title="Reload run status"><IconButton onClick={reloadRuns} size="small"><RefreshIcon /></IconButton></Tooltip>
                </Stack>
                {runs.length === 0 ? <Typography color="text.secondary">No bulk deletions have been scheduled.</Typography> :
                    runs.map(run => <Paper key={run.id} variant="outlined" sx={{ p: 1.5 }}>
                        <Stack spacing={0.75}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1}>
                                <Typography variant="subtitle1"><strong>Bulk Delete</strong> · {run.versionName}</Typography>
                                <Chip size="small" label={run.status.replace(/([a-z])([A-Z])/g, '$1 $2')} color={statusColour(run.status)} />
                            </Stack>
                            <Typography variant="body2">Deleted {run.deletedCount} · failed {run.failedCount} · total {run.totalCount}</Typography>
                            <Typography variant="caption" color="text.secondary">
                                Requested by {run.requestedBy || 'Unknown'} at {displayDate(run.requestedUtc)} · scheduled {displayDate(run.scheduledUtc)} · started {displayDate(run.startedUtc)} · completed {displayDate(run.completedUtc)}
                            </Typography>
                            {run.summary && <Typography variant="body2">{run.summary}</Typography>}
                            {run.failures.length > 0 && <Box>
                                <Typography variant="body2" color="error.main"><strong>Failed sites</strong></Typography>
                                {run.failures.map(failure => <Typography key={failure.siteReference} variant="caption" display="block" color="error.main">
                                    {failure.siteReference}: {failure.diagnostic || 'No diagnostic was recorded.'}
                                </Typography>)}
                            </Box>}
                        </Stack>
                    </Paper>)}
            </Stack>
        </DialogContent>
        <DialogActions>
            <Button onClick={close} disabled={submitting}>Close</Button>
            {!preview && <Button variant="contained" onClick={review} disabled={!canReview}>Review CSV</Button>}
            {preview && <Button variant="outlined" onClick={review} disabled={!canReview}>Refresh review</Button>}
            {preview && <Button variant="contained" color="error" onClick={() => setConfirming(true)} disabled={!canSchedule}>Review deletion</Button>}
        </DialogActions>

        <Dialog open={confirming} onClose={() => !submitting && setConfirming(false)} maxWidth="sm" fullWidth>
            <MaintenanceDialogTitle icon={<ReportProblemOutlinedIcon />} title="Final deletion confirmation"
                description="This operation cannot be undone" tone="error" />
            <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    <Alert severity="error">
                        Permanently delete <strong>{preview?.sitesToDelete.length}</strong> sites and <strong>{preview?.totalRecordCount}</strong> related records from <strong>{preview?.version.versionName}</strong>?
                    </Alert>
                    <Typography variant="body2">{preview?.startsImmediately
                        ? 'The job will be enqueued immediately.'
                        : `The job is scheduled for ${preview?.scheduledLocal} UK time.`}</Typography>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setConfirming(false)} disabled={submitting}>Back</Button>
                <Button onClick={schedule} disabled={submitting} variant="contained" color="error">Schedule permanent deletion</Button>
            </DialogActions>
        </Dialog>
    </Dialog>;
}
